import { enums, enumsTypes } from "@shared/constants";
import { ItemTypesMapIdType } from "@shared/constants/enums/enums-types";
import { DEFAULT_CURRENCY } from "@shared/constants/financials";
import useDjangoWaffle from "@shared/hooks/djangoWaffle/useDjangoWaffle";
import { PwycPriceManager } from "@shared/modules/seating/services/PwycPriceManager";
import { TicketTypeSerializer } from "@shared/modules/events/types/public";
import {
  getPrice,
  getPriceRange,
} from "@shared/modules/financials/utils/price-utils";
import { BestAvailableSeatingProps } from "@shared/modules/seating/components/SidebarSeatingContainer/SidebarSeatingContainer.props";
import { useSeatingState } from "@shared/modules/seating/contexts/useSeatingState";
import usePublicBasedTTSeatPermissions from "@shared/modules/seating/services/usePublicBasedTTSeatPermissions";
import { SeatingItemPropertyMap } from "@shared/modules/seating/types/seating-property-types";
import {
  BestAvailableItemType,
  BestAvailableLocationType,
  SingleBestAvailableSeatingType,
} from "@shared/modules/seating/types/seating-types";

import { useTranslation } from "@shared/utils/internationalization";

export default function useBestAvailableSeatingData({
  allInPricingEnabled,
  currency = DEFAULT_CURRENCY,
  venueId,
  paymentType,
  purchaseSourcePlatform,
  // needed because backend cant tell if we are on box office without using flaky methods
  // this is to be able to use box office purchase limits for best available seating when on box office
  isBoxOffice = false,
}: {
  allInPricingEnabled: boolean;
  currency: enumsTypes.CurrencyMapIdType;
  venueId: number;
  paymentType: number | undefined;
  purchaseSourcePlatform: string | undefined;
  isBoxOffice?: boolean;
}) {
  const { isFlagActive } = useDjangoWaffle();
  const { t } = useTranslation("seating");
  const {
    bestAvailableSeatingQueryParams,
    seatingItemArray,
    seatingServiceState,
  } = useSeatingState();
  const { useGetBestAvailableSeating, invalidateAllRepositoryQueries } =
    usePublicBasedTTSeatPermissions();
  const pwycPriceManager = PwycPriceManager.getInstance();

  const isBestAvailableSeatingEnabled = () => {
    const {
      event,
      membership_group,
      package: packageId,
      membership_benefit,
      allPermissions,
      quantity,
    } = bestAvailableSeatingQueryParams;

    if (!event && !membership_group && !packageId) {
      return false;
    }
    if (!isFlagActive("enable_best_assigned_seating", venueId) || !quantity) {
      return false;
    }

    if (membership_group) {
      return (
        Boolean(membership_benefit) &&
        Boolean(allPermissions !== undefined) &&
        Boolean(
          isFlagActive("enable_memberships_best_available_seating", venueId)
        ) === Boolean(membership_group)
      );
    }

    return true;
  };

  const {
    data: bestAvailableSeating,
    isLoading: isBestAvailableSeatingLoading,
  } = useGetBestAvailableSeating(bestAvailableSeatingQueryParams, isBoxOffice, {
    enabled: isBestAvailableSeatingEnabled(),
  });

  const getPriceFromSeatPermission = (
    seat: SingleBestAvailableSeatingType,
    id: number,
    defaultPrice?: string | number
  ): string => {
    const itemType = seat.ttsps[0].ticket_type_id
      ? enums.ITEM_TYPES_MAP.ITEM_TYPE_TICKET.id
      : enums.ITEM_TYPES_MAP.ITEM_TYPE_MEMBERSHIP.id;

    const price = pwycPriceManager.getPwycPrice(id, itemType);

    return price ?? defaultPrice?.toString() ?? "0";
  };

  const getPriceFromLocationPermission = (
    location: BestAvailableLocationType[0],
    id: number,
    defaultPrice?: string | number
  ): string => {
    const itemType = location.ttlps[0].ticket_type_id
      ? enums.ITEM_TYPES_MAP.ITEM_TYPE_TICKET.id
      : enums.ITEM_TYPES_MAP.ITEM_TYPE_MEMBERSHIP.id;

    const price = pwycPriceManager.getPwycPrice(id, itemType);

    return price ?? defaultPrice?.toString() ?? "0";
  };

  const getPriceMap = (
    seats: BestAvailableItemType["seat_options"][0],
    ticketTypeItems: SeatingItemPropertyMap[] | undefined
  ): Record<number, string> => {
    const priceMap: Record<number, string> = {};
    seats.seat_group.forEach((seat) => {
      const ticketTypeItem = ticketTypeItems?.find(
        (item) =>
          item.id === seat.ttsps[0].ticket_type_id ||
          item.id === seat.ttsps[0].membership_level_id
      );

      if (!ticketTypeItem) {
        return;
      }

      const isPwycTicket = Boolean(
        ticketTypeItem?.pay_what_you_can_configuration
      );

      const basePrice = getPrice(
        ticketTypeItem as TicketTypeSerializer,
        purchaseSourcePlatform || "",
        paymentType || 0,
        !isPwycTicket // pwyc is currently not using all-in-pricing
      );

      priceMap[seat.id] = !isPwycTicket
        ? String(basePrice)
        : getPriceFromSeatPermission(seat, ticketTypeItem?.id, basePrice);
    });
    return priceMap;
  };

  const getSeatTicketTypeMap = (
    seats: BestAvailableItemType["seat_options"][0]
  ) => {
    const seatTicketTypeMap: Record<number, number> = {};
    seats.seat_group.forEach((seat) => {
      seatTicketTypeMap[seat.id] = seat.ttsps[0].ticket_type_id;
    });
    return seatTicketTypeMap;
  };

  const getSeatMembershipLevelMap = (
    seats: BestAvailableItemType["seat_options"][0]
  ) => {
    const seatMembershipLevelMap: Record<number, number> = {};
    seats.seat_group.forEach((seat) => {
      seatMembershipLevelMap[seat.id] = seat.ttsps[0].membership_level_id;
    });
    return seatMembershipLevelMap;
  };

  const getSeatPermissionMap = (
    seats: BestAvailableItemType["seat_options"][0],
    ticketTypeItems: SeatingItemPropertyMap[] | undefined
  ) => {
    const seatPermissionMap: Record<number, number> = {};

    seats.seat_group.forEach((seat) => {
      const permission = seat.ttsps.find((ttsp) =>
        ticketTypeItems?.some(
          (item) =>
            item.id === ttsp.ticket_type_id ||
            item.id === ttsp.membership_level_id
        )
      )?.id;
      if (permission) {
        seatPermissionMap[seat.id] = permission;
      }
    });
    return seatPermissionMap;
  };

  const getLocationPermissionsMap = (
    location: BestAvailableLocationType[0],
    ticketTypeItems: SeatingItemPropertyMap[] | undefined
  ) => {
    const newLocation = {
      ...location,
      ttlps: location.ttlps.filter((ttlp) =>
        ticketTypeItems?.some(
          (item) =>
            item.id === ttlp.ticket_type_id ||
            item.id === ttlp.membership_level_id
        )
      ),
    };

    return {
      [location.id]: newLocation.ttlps.map((ttlp) => {
        const locationPermission =
          seatingServiceState.locationMap[newLocation.id];
        return locationPermission?.find(
          (permission) =>
            permission.seatingItemTypeId ===
              (ttlp.ticket_type_id || ttlp.membership_level_id) &&
            ticketTypeItems?.some(
              (item) =>
                item.id === ttlp.ticket_type_id ||
                item.id === ttlp.membership_level_id
            )
        )?.seatingItemPermissionId;
      }),
    };
  };

  const getLocationTicketTypesMap = (
    location: BestAvailableLocationType[0],
    ticketTypeItems: SeatingItemPropertyMap[] | undefined
  ) => {
    const newLocation = {
      ...location,
      ttlps: location.ttlps.filter((ttlp) =>
        ticketTypeItems?.some(
          (item) =>
            item.id === ttlp.ticket_type_id ||
            item.id === ttlp.membership_level_id
        )
      ),
    };
    return {
      [location.id]: newLocation.ttlps.map((ttlp) => ttlp.ticket_type_id),
    };
  };

  const getLocationPricingMap = (
    location: BestAvailableLocationType[0],
    ticketTypeItems: SeatingItemPropertyMap[] | undefined
  ) => {
    const newLocation = {
      ...location,
      ttlps: location.ttlps.filter((ttlp) =>
        ticketTypeItems?.some(
          (item) =>
            item.id === ttlp.ticket_type_id ||
            item.id === ttlp.membership_level_id
        )
      ),
    };
    return {
      [location.id]: newLocation.ttlps.map((ttlp) => {
        const ticketTypeItem = ticketTypeItems?.find(
          (item) =>
            item.id === ttlp.ticket_type_id ||
            item.id === ttlp.membership_level_id
        );
        const isPwycTicket = Boolean(
          ticketTypeItem?.pay_what_you_can_configuration
        );

        const basePrice = ticketTypeItem
          ? getPrice(
              ticketTypeItem as TicketTypeSerializer,
              purchaseSourcePlatform || "",
              paymentType || 0,
              !isPwycTicket // pwyc is currently not using all-in-pricing
            )
          : undefined;

        return ticketTypeItem && !isPwycTicket
          ? basePrice?.toString() ?? "0"
          : getPriceFromLocationPermission(
              location,
              ticketTypeItem?.id,
              basePrice
            );
      }),
    };
  };

  const injectPwycPrice = (
    ticketTypeItems: TicketTypeSerializer[],
    itemType: ItemTypesMapIdType
  ) => {
    const newTicketTypeItems = ticketTypeItems.map((item) => {
      const price =
        pwycPriceManager.getPwycPrice(item.id, itemType) || item.price;
      return {
        ...item,
        $pwycPrice: price ? Number(price) : undefined,
      };
    });
    return newTicketTypeItems;
  };

  const getLocationMembershipLevelsMap = (
    location: BestAvailableLocationType[0]
  ) => ({
    [location.id]: location.ttlps.map((ttlp) => ttlp.membership_level_id),
  });

  const bestAvailableData:
    | BestAvailableSeatingProps["bestAvailableSeating"]
    | undefined = (() => {
    if (!bestAvailableSeating) return undefined;

    // Handle seats and locations separately since they have different structures
    const processSeats = (seatOptions: BestAvailableItemType["seat_options"]) =>
      seatOptions.reduce((acc, seats) => {
        const seatsTicketTypes: number[] = seats.seat_group.reduce(
          (acc, seat) => {
            const ticketTypes: typeof seat.ttsps = seat?.ttsps;

            if (ticketTypes.length > 0) {
              return acc.concat(
                ticketTypes.map((tt: (typeof ticketTypes)[0]) => {
                  return bestAvailableSeatingQueryParams.membership_group
                    ? tt.membership_level_id
                    : tt.ticket_type_id;
                })
              );
            }
            return acc;
          },
          [] as number[]
        );

        const ticketTypeItems = seatingItemArray?.filter((item) =>
          seatsTicketTypes.includes(item.id)
        );

        if (!ticketTypeItems?.length) return acc;

        const itemType = bestAvailableSeatingQueryParams.membership_group
          ? enums.ITEM_TYPES_MAP.ITEM_TYPE_MEMBERSHIP.id
          : enums.ITEM_TYPES_MAP.ITEM_TYPE_TICKET.id;

        const seatsMaps = {
          permissionsMap: getSeatPermissionMap(seats, ticketTypeItems),
          pricingMap: getPriceMap(seats, ticketTypeItems),
          membershipLevelsMap: getSeatMembershipLevelMap(seats),
          ticketTypesMap: getSeatTicketTypeMap(seats),
        };

        acc.push({
          label: `${t("Section")} ${seats.seat_group[0]?.segment_identifier}`,
          id: seats.seat_group[0]?.id,
          price: getPriceRange({
            items: injectPwycPrice(
              ticketTypeItems as TicketTypeSerializer[],
              itemType
            ),
            allInPricing: allInPricingEnabled,
            venueCurrency: currency,
            paymentType,
            purchasePlatform: purchaseSourcePlatform,
          }),
          seatingInfo: {
            identifier: seats.seat_group
              .flatMap((seat) => seat?.identifier)
              .join(", "),
            seatIds: seats.seat_group.flatMap((seat) => seat?.id),
            segment_identifier: seats.seat_group[0]?.segment_identifier ?? "",
            location_identifier: seats.seat_group[0]?.location_identifier ?? "",
            is_accessible:
              seats.seat_group.some((seat) => seat?.is_accessible) || false,
            ...seatsMaps,
          },
          score: seats.score,
        });
        return acc;
      }, [] as BestAvailableSeatingProps["bestAvailableSeating"]);

    const processLocations = (locations: BestAvailableLocationType) =>
      locations.reduce((acc, location) => {
        const ticketTypes = location?.ttlps;
        const locationTicketTypes: number[] = ticketTypes.map((tt) => {
          return bestAvailableSeatingQueryParams.membership_group
            ? tt.membership_level_id
            : tt.ticket_type_id;
        });

        const ticketTypeItems = seatingItemArray?.filter((item) =>
          locationTicketTypes.includes(item.id)
        );

        if (!ticketTypeItems?.length) return acc;

        const itemType = bestAvailableSeatingQueryParams.membership_group
          ? enums.ITEM_TYPES_MAP.ITEM_TYPE_MEMBERSHIP.id
          : enums.ITEM_TYPES_MAP.ITEM_TYPE_TICKET.id;

        const locationMaps = {
          permissionsMap: getLocationPermissionsMap(location, ticketTypeItems),
          ticketTypesMap: getLocationTicketTypesMap(location, ticketTypeItems),
          pricingMap: getLocationPricingMap(location, ticketTypeItems),
          membershipLevelsMap: getLocationMembershipLevelsMap(location),
        };

        acc.push({
          label: `${t("Section")} ${location?.segment_identifier}`,
          id: location.id,
          price: getPriceRange({
            items: injectPwycPrice(
              ticketTypeItems as TicketTypeSerializer[],
              itemType
            ),
            allInPricing: allInPricingEnabled,
            venueCurrency: currency,
            paymentType,
            purchasePlatform: purchaseSourcePlatform,
          }),
          gaInfo: {
            identifier: location.identifier,
            segment_identifier: location?.segment_identifier ?? "",
            location_identifier: location?.location_identifier ?? "",
            ...locationMaps,
          },
          score: location.score,
        });
        return acc;
      }, [] as BestAvailableSeatingProps["bestAvailableSeating"]);

    return [
      ...processSeats(bestAvailableSeating.seat_options),
      ...processLocations(bestAvailableSeating.location_options),
    ];
  })();

  // sort seats by score, higher is better
  const sortedBestAvailableData = bestAvailableData?.sort(
    (a, b) => b.score - a.score
  );

  return {
    bestAvailableData: sortedBestAvailableData,
    isBestAvailableSeatingLoading,
    invalidateAllRepositoryQueries,
  };
}
